import { TenantWorkflow } from "./tenant-workflow";
// Owner feature/quota editor (#239) — per-business capability toggles + quota limits over
// /api/owner/tenants/:id/features, and the global defaults over /api/owner/feature-defaults.
// The API is the source of truth: it resolves env kill-switch AND status AND toggle AND quota
// and returns the effective value + provenance; this UI only edits the tenant-toggle/limits
// layer. Owner-only. "Business/Betrieb" is display copy; code says tenant.
import React from "react";
import { Banner, Button, Dialog, Input, StatusPill, Switch } from "@/ds";
import { Icon } from "@/lib";
import { cockpit } from "@/api";
import type {
  FeatureDefaults,
  FeatureKey,
  GooglePlacesQuotaState,
  OwnerTenantFeatures,
  OwnerTenantFeatureView,
  QuotaKey,
  TenantRow,
} from "@/api";
import { useT } from "../i18n";

/** UI metadata mirroring the server catalog (tenancy/tenant-features.ts). The server CHECK +
 *  resolver stay authoritative; this only drives rendering order and which limit fields exist. */
const FEATURE_ORDER: FeatureKey[] = [
  "ingest",
  "ai_extraction",
  "draft_compose",
  "single_agent_workflow",
  "send",
  "external_lookup",
  "positions_offer",
  "rule_values_edit",
  "test_injection",
];
/** These capabilities require an explicit grant for one business. */
const PER_TENANT_ONLY_FEATURES: FeatureKey[] = ["external_lookup", "test_injection", "single_agent_workflow"];
const DEFAULTS_FEATURE_ORDER: FeatureKey[] = FEATURE_ORDER.filter(
  (key) => !PER_TENANT_ONLY_FEATURES.includes(key),
);
const FEATURE_QUOTAS: Partial<Record<FeatureKey, QuotaKey[]>> = {
  send: ["sends_per_day"],
  ai_extraction: ["ai_calls_per_month"],
  external_lookup: ["google_places_requests_per_month"],
};
/** Warning copy under a feature name, for capabilities whose enablement changes behavior in a
 *  way the owner must not trip over by accident. */
const FEATURE_HINTS: Partial<Record<FeatureKey, string>> = {
  rule_values_edit: "businesses.feature.rule_values_edit.hint",
  test_injection: "businesses.feature.test_injection.hint",
  single_agent_workflow: "businesses.feature.single_agent_workflow.hint",
};

type Toast = (t: { tone?: string; title?: string; msg?: React.ReactNode }) => void;

/** The explicitly-overridden limits of a feature (source === "override") — what we resend so a
 *  toggle/limit edit never silently promotes an inherited default value into an override. */
function overrideLimitsOf(f: OwnerTenantFeatureView): Partial<Record<QuotaKey, number>> {
  const out: Partial<Record<QuotaKey, number>> = {};
  for (const q of f.quotas) if (q.source === "override") out[q.key] = q.limit;
  return out;
}

/** Inline integer field that commits on blur/Enter only when the value actually changed. */
function LimitField({
  value,
  disabled,
  max,
  onCommit,
}: {
  value: number;
  disabled?: boolean;
  max?: number;
  onCommit: (n: number) => void;
}) {
  const [draft, setDraft] = React.useState(String(value));
  React.useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const n = Number(draft);
    if (Number.isInteger(n) && n >= 0 && (max === undefined || n <= max) && n !== value) onCommit(n);
    else setDraft(String(value));
  };
  return (
    <Input
      type="number"
      min={0}
      max={max}
      value={draft}
      disabled={disabled}
      className="tenant-features-limit-input"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

export interface TenantFeaturesEditorProps {
  /** #378: rendered inline as a business-detail section (was a dialog). */
  tenantId: string;
  pushToast: Toast;
}

export function TenantFeaturesEditor({ tenantId, pushToast }: TenantFeaturesEditorProps) {
  const t = useT();
  const [data, setData] = React.useState<OwnerTenantFeatures | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyKey, setBusyKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    setData(null);
    setError(null);
    if (!tenantId) return;
    cockpit
      .getTenantFeatures(tenantId)
      .then(setData)
      .catch((e: any) => setError(e?.message || t("common.error")));
  }, [tenantId, t]);

  const apply = (key: string, p: Promise<OwnerTenantFeatures>) => {
    setBusyKey(key);
    p.then((r) => setData(r))
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setBusyKey(null));
  };

  const byKey = new Map((data?.features ?? []).map((f) => [f.key, f]));

  return (
      <div className="stack-sm tenant-features-dialog__content" style={{ paddingBottom: "var(--space-4)" }} data-testid="features-editor">
        <TenantWorkflow key={`${tenantId}:${byKey.get("single_agent_workflow")?.enabled}`} tenantId={tenantId} />
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
        {!data && !error && <p className="subtle">{t("common.loading")}</p>}
        {data && (
          <>
            <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
              {t("businesses.features.hint")}
            </p>
            <div
              className="tenant-features-table-scroll tenant-features-table-scroll--catalog"
              data-testid="tenant-features-table-scroll"
              role="region"
              aria-label={t("businesses.features.title")}
              tabIndex={0}
            >
              <table className="neo-table reqventos-feature-table">
                <thead>
                  <tr>
                    <th>{t("businesses.features.colFeature")}</th>
                    <th>{t("businesses.features.colStatus")}</th>
                    <th>{t("businesses.features.colToggle")}</th>
                    <th>{t("businesses.features.colLimits")}</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_ORDER.map((key) => {
                    const f = byKey.get(key);
                    if (!f) return null;
                    const rowBusy = busyKey === key;
                    const overrides = overrideLimitsOf(f);
                    return (
                      <tr key={key}>
                        <td data-label={t("businesses.features.colFeature")}>
                          <div className="reqventos-feature-name">
                          <b>{t(`businesses.feature.${key}`, undefined, key)}</b>
                          {f.overridden && (
                              <button
                                className="linkbtn"
                                disabled={rowBusy}
                                onClick={() => apply(key, cockpit.clearTenantFeature(tenantId, key))}
                              >
                                {t("businesses.features.reset")}
                              </button>
                          )}
                          </div>
                          {FEATURE_HINTS[key] && (
                            <div className="subtle" style={{ fontSize: "var(--text-sm)" }}>
                              {t(FEATURE_HINTS[key]!)}
                            </div>
                          )}
                        </td>
                        <td data-label={t("businesses.features.colStatus")}>
                          <StatusPill
                            tone={f.enabled ? "success" : "warning"}
                            label={t(f.enabled ? "businesses.features.on" : "businesses.features.off")}
                          />
                          {f.has_env_flag && (
                            <div className="subtle" style={{ fontSize: "var(--text-sm)" }}>
                              {t("businesses.features.globalEnv")}: {t(
                                f.env_enabled ? "businesses.features.on" : "businesses.features.off",
                              )}
                            </div>
                          )}
                          {!f.enabled && (
                            <span className="subtle" style={{ fontSize: "var(--text-sm)" }}>
                              {" "}
                              · {t(`businesses.features.reason.${blockReason(f, data)}`, undefined, "")}
                            </span>
                          )}
                        </td>
                        <td data-label={t("businesses.features.colToggle")}>
                          <Switch
                            checked={f.feature_enabled}
                            disabled={rowBusy}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              apply(
                                key,
                                cockpit.setTenantFeature(tenantId, key, {
                                  enabled: e.currentTarget.checked,
                                  limits: overrides,
                                }),
                              )
                            }
                          />
                        </td>
                        <td data-label={t("businesses.features.colLimits")}>
                          {f.quotas.length === 0 && <span className="subtle">—</span>}
                          <div className="stack-xs">
                            {f.quotas.map((q) => (
                              <label
                                key={q.key}
                                className="subtle reqventos-quota-field"
                                // #935: the inherited daily send ceiling ramps with the business's
                                // age, so say so wherever it is still inherited.
                                title={
                                  q.key === "sends_per_day" && q.source === "catalog"
                                    ? t("businesses.quota.sendsWarmup")
                                    : undefined
                                }
                              >
                                <span>{t(`businesses.quota.${q.key}`, undefined, q.key)}</span>
                                <span className="reqventos-quota-value">
                                <span
                                  className="subtle"
                                  style={{ fontVariantNumeric: "tabular-nums" }}
                                  title={t("businesses.quota.usageTitle")}
                                >
                                  {q.usage} /
                                </span>
                                <LimitField
                                  value={q.limit}
                                  disabled={rowBusy}
                                  max={q.key === "google_places_requests_per_month" ? 1000 : undefined}
                                  onCommit={(n) =>
                                    apply(
                                      key,
                                      cockpit.setTenantFeature(tenantId, key, {
                                        enabled: f.feature_enabled,
                                        limits: { ...overrides, [q.key]: n },
                                      }),
                                    )
                                  }
                                />
                                </span>
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {data.plan_quotas.length > 0 && (
              <>
                <p className="subtle" style={{ margin: "var(--space-2) 0 0", fontSize: "var(--text-sm)" }}>
                  {t("businesses.features.planTitle")}
                </p>
                <div
                  className="tenant-features-table-scroll"
                  data-testid="tenant-features-table-scroll"
                  role="region"
                  aria-label={t("businesses.features.planTitle")}
                  tabIndex={0}
                >
                  <table className="neo-table">
                    <thead>
                      <tr>
                        <th>{t("businesses.features.colQuota")}</th>
                        <th>{t("businesses.features.colUsageLimit")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.plan_quotas.map((pq) => {
                        const rowBusy = busyKey === `plan:${pq.key}`;
                        return (
                          <tr key={pq.key}>
                            <td data-label={t("businesses.features.colQuota")}>
                              <b>{t(`businesses.planQuota.${pq.key}`, undefined, pq.key)}</b>
                            </td>
                            <td data-label={t("businesses.features.colUsageLimit")}>
                              <label
                                className="subtle reqventos-quota-value"
                              >
                                <span
                                  className="subtle"
                                  style={{ fontVariantNumeric: "tabular-nums" }}
                                  title={t("businesses.quota.usageTitle")}
                                >
                                  {pq.usage} /
                                </span>
                                <LimitField
                                  value={pq.limit}
                                  disabled={rowBusy}
                                  onCommit={(n) =>
                                    apply(`plan:${pq.key}`, cockpit.setPlanLimits(tenantId, { [pq.key]: n }))
                                  }
                                />
                              </label>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
  );
}

/** Why a feature resolved closed (for the status hint). */
function blockReason(f: OwnerTenantFeatureView, data: OwnerTenantFeatures): string {
  if (!f.env_enabled) return "env";
  if (!f.tenant_active) return "suspended";
  if (!f.feature_enabled) return "disabled";
  if (
    f.key === "external_lookup" &&
    data.google_places_global_quota.usage >= data.google_places_global_quota.monthlyLimit
  ) return "globalQuota";
  return "quota";
}

export interface FeatureDefaultsDialogProps {
  open: boolean;
  onClose: () => void;
  pushToast: Toast;
}

type EnabledChoice = "" | "on" | "off";

export function FeatureDefaultsDialog({ open, onClose, pushToast }: FeatureDefaultsDialogProps) {
  const t = useT();
  const [draft, setDraft] = React.useState<FeatureDefaults | null>(null);
  const [googleQuota, setGoogleQuota] = React.useState<GooglePlacesQuotaState | null>(null);
  const [googleLimitDraft, setGoogleLimitDraft] = React.useState("0");
  const [error, setError] = React.useState<string | null>(null);
  const [defaultsError, setDefaultsError] = React.useState<string | null>(null);
  const [quotaError, setQuotaError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [quotaBusy, setQuotaBusy] = React.useState(false);

  React.useEffect(() => {
    setDraft(null);
    setGoogleQuota(null);
    setError(null);
    setDefaultsError(null);
    setQuotaError(null);
    if (!open) return;
    Promise.all([cockpit.getFeatureDefaults(), cockpit.getGooglePlacesQuota()])
      .then(([defaults, quota]) => {
        setDraft(defaults ?? {});
        setGoogleQuota(quota);
        setGoogleLimitDraft(String(quota.monthlyLimit));
      })
      .catch((e: any) => setError(e?.message || t("common.error")));
  }, [open, t]);

  const setEnabled = (key: FeatureKey, choice: EnabledChoice) => {
    setDraft((prev) => {
      const next = { ...(prev ?? {}) };
      const entry = { ...(next[key] ?? {}) };
      if (choice === "") delete entry.enabled;
      else entry.enabled = choice === "on";
      if (entry.enabled === undefined && !entry.limits) delete next[key];
      else next[key] = entry;
      return next;
    });
  };
  const setLimit = (key: FeatureKey, q: QuotaKey, raw: string) => {
    setDraft((prev) => {
      const next = { ...(prev ?? {}) };
      const entry = { ...(next[key] ?? {}) };
      const limits = { ...(entry.limits ?? {}) };
      const n = Number(raw);
      if (raw.trim() === "" || !Number.isInteger(n) || n < 0) delete limits[q];
      else limits[q] = n;
      if (Object.keys(limits).length === 0) delete entry.limits;
      else entry.limits = limits;
      if (entry.enabled === undefined && !entry.limits) delete next[key];
      else next[key] = entry;
      return next;
    });
  };

  const save = () => {
    if (!draft) return;
    setBusy(true);
    setDefaultsError(null);
    cockpit
      .setFeatureDefaults(draft)
      .then((d) => {
        setDraft(d ?? {});
        pushToast({ tone: "success", title: t("businesses.features.defaultsSaved") });
        onClose();
      })
      .catch((e: any) => setDefaultsError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  const saveGoogleQuota = () => {
    if (!googleQuota) return;
    const monthlyLimit = Number(googleLimitDraft);
    if (
      !Number.isInteger(monthlyLimit) ||
      monthlyLimit < 0 ||
      monthlyLimit > googleQuota.hardCeiling
    ) {
      setQuotaError(t("businesses.features.googleQuotaInvalid", { max: googleQuota.hardCeiling }));
      return;
    }
    setQuotaBusy(true);
    setQuotaError(null);
    cockpit
      .setGooglePlacesQuota(monthlyLimit)
      .then((quota) => {
        setGoogleQuota(quota);
        setGoogleLimitDraft(String(quota.monthlyLimit));
        pushToast({ tone: "success", title: t("businesses.features.googleQuotaSaved") });
      })
      .catch((e: any) => setQuotaError(e?.message || t("common.error")))
      .finally(() => setQuotaBusy(false));
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      wide
      className="tenant-features-dialog"
      tone="navy"
      icon={<Icon name="settings" size={20} />}
      title={t("businesses.features.defaultsTitle")}
      footer={
        <div className="management-actions management-actions--end">
          <Button variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      }
    >
      <div className="stack-sm tenant-features-dialog__content" style={{ paddingBottom: "var(--space-4)" }}>
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
        {(!draft || !googleQuota) && !error && <p className="subtle">{t("common.loading")}</p>}
        {draft && googleQuota && (
          <>
            <section className="reqventos-defaults-group" aria-labelledby="reqventos-global-quota-title">
              <h3 id="reqventos-global-quota-title">{t("businesses.features.googleQuotaTitle")}</h3>
              <p className="subtle">{t("businesses.features.googleQuotaSaveScope")}</p>
              {quotaError && <Banner tone="danger" title={t("common.error")}>{quotaError}</Banner>}
              <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                {t("businesses.features.googleQuotaHint", { max: googleQuota.hardCeiling })}
              </p>
              <div className="management-field-action">
                <span className="subtle" style={{ fontVariantNumeric: "tabular-nums", alignSelf: "center" }}>
                  {t("businesses.features.googleQuotaUsage")}: {googleQuota.usage} /
                </span>
                <Input
                  aria-label={t("businesses.features.googleQuotaLimit")}
                  type="number"
                  min={0}
                  max={googleQuota.hardCeiling}
                  value={googleLimitDraft}
                  disabled={quotaBusy}
                  className="tenant-features-limit-input"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGoogleLimitDraft(e.target.value)}
                />
                <Button loading={quotaBusy} variant="secondary" disabled={quotaBusy} onClick={saveGoogleQuota}>
                  {t("businesses.features.googleQuotaSave")}
                </Button>
              </div>
              <span className="subtle" style={{ fontSize: "var(--text-sm)" }}>
                {t("businesses.features.googleQuotaWindow", {
                  from: googleQuota.windowStart.slice(0, 10),
                  to: googleQuota.windowEnd.slice(0, 10),
                })}
              </span>
            </section>
            <section className="reqventos-defaults-group" aria-labelledby="reqventos-business-defaults-title">
            <h3 id="reqventos-business-defaults-title">{t("businesses.features.businessDefaultsTitle")}</h3>
            <p className="subtle">{t("businesses.features.defaultsHint")}</p>
            <div
              className="tenant-features-table-scroll tenant-features-table-scroll--catalog"
              role="region"
              aria-label={t("businesses.features.defaultsTitle")}
              tabIndex={0}
            >
              <table className="neo-table">
                <thead>
                  <tr>
                    <th>{t("businesses.features.colFeature")}</th>
                    <th>{t("businesses.features.colDefault")}</th>
                    <th>{t("businesses.features.colLimits")}</th>
                  </tr>
                </thead>
                <tbody>
                  {DEFAULTS_FEATURE_ORDER.map((key) => {
                    const entry = draft[key] ?? {};
                    const choice: EnabledChoice =
                      entry.enabled === undefined ? "" : entry.enabled ? "on" : "off";
                    return (
                      <tr key={key}>
                        <td data-label={t("businesses.features.colFeature")}>
                          <b>{t(`businesses.feature.${key}`, undefined, key)}</b>
                        </td>
                        <td data-label={t("businesses.features.colDefault")}>
                          <select
                            className="neo-input reqventos-defaults-select"
                            aria-label={t(`businesses.feature.${key}`, undefined, key)}
                            value={choice}
                            onChange={(e) => setEnabled(key, e.target.value as EnabledChoice)}
                          >
                            <option value="">{t("businesses.features.inherit")}</option>
                            <option value="on">{t("businesses.features.on")}</option>
                            <option value="off">{t("businesses.features.off")}</option>
                          </select>
                        </td>
                        <td data-label={t("businesses.features.colLimits")}>
                          {(FEATURE_QUOTAS[key] ?? []).length === 0 && <span className="subtle">—</span>}
                          <div className="stack-xs">
                            {(FEATURE_QUOTAS[key] ?? []).map((q) => (
                              <label key={q} className="subtle reqventos-quota-field">
                                <span>{t(`businesses.quota.${q}`, undefined, q)}</span>
                                <Input
                                  type="number"
                                  min={0}
                                  max={q === "google_places_requests_per_month" ? 1000 : undefined}
                                  placeholder="999"
                                  value={entry.limits?.[q] ?? ""}
                                  className="tenant-features-limit-input"
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLimit(key, q, e.target.value)}
                                />
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {defaultsError && <Banner tone="danger" title={t("common.error")}>{defaultsError}</Banner>}
            <div className="management-actions">
              <Button loading={busy} variant="primary" disabled={busy || !draft} onClick={save}>
                {t("businesses.features.saveDefaults")}
              </Button>
            </div>
            </section>
          </>
        )}
      </div>
    </Dialog>
  );
}
